// ─────────────────────────────────────────────────────────────────────────────
// cast_curves.ts — Canonical cast trace curves for every spell
//
// Each spell has a normalized polyline (points in 0–1 space) representing the
// wand-mode gesture the user traces to cast it.  After the Thrice Knock
// invocation activates wand mode, the user draws one of these shapes and
// the classifier identifies the spell on pen-up.
//
// No self-crossing knot prefix — the stroke IS the spell.
//
// Coordinates are normalized: (0,0) = top-left, (1,1) = bottom-right of the
// display region.  The renderer scales them to the target bounding box.
// ─────────────────────────────────────────────────────────────────────────────

export interface CastCurve {
  /** Normalized polyline control points [x, y] in 0–1 space. */
  points: [number, number][];
  /** Whether the curve closes back on itself (lasso-type spells). */
  closed: boolean;
}

// ── Curve dictionary ─────────────────────────────────────────────────────────
// Keys match spell IDs.  Every discoverable spell should have an entry.

export const CAST_CURVES: Record<string, CastCurve> = {

  // ── Act family ───────────────────────────────────────────────────────────

  // erase.region — lasso encircling a region, then a slash through it
  'erase.region': {
    points: [
      [0.30, 0.28], [0.18, 0.48], [0.23, 0.72], [0.45, 0.83],
      [0.70, 0.76], [0.80, 0.52], [0.72, 0.30], [0.48, 0.22],
      [0.30, 0.28],   // lasso closes
      [0.35, 0.65],   // slash start
      [0.70, 0.32],   // slash end
    ],
    closed: false,
  },

  // select.point — short downward tap on a target
  'select.point': {
    points: [
      [0.50, 0.30], [0.50, 0.45], [0.50, 0.58],
    ],
    closed: false,
  },

  // derive.step — lasso then downward tail
  'derive.step': {
    points: [
      [0.30, 0.15], [0.18, 0.30], [0.22, 0.48], [0.42, 0.55],
      [0.58, 0.48], [0.58, 0.34], [0.42, 0.18], [0.30, 0.15],
      [0.42, 0.55], [0.42, 0.70], [0.42, 0.85],
    ],
    closed: false,
  },

  // ── Bind family ──────────────────────────────────────────────────────────

  // annotate.link — mostly-straight line connecting two points
  'annotate.link': {
    points: [
      [0.12, 0.35], [0.28, 0.40], [0.50, 0.50],
      [0.72, 0.60], [0.88, 0.65],
    ],
    closed: false,
  },

  // ── Transmute family ─────────────────────────────────────────────────────

  // block.duplicate — figure-8 / self-crossing loop
  'block.duplicate': {
    points: [
      [0.50, 0.28], [0.35, 0.42], [0.30, 0.58], [0.38, 0.72],
      [0.52, 0.64], [0.58, 0.48],
      [0.64, 0.36], [0.70, 0.52], [0.62, 0.70],
      [0.46, 0.72], [0.34, 0.55], [0.50, 0.28],
    ],
    closed: true,
  },

  // ── Invoke family ────────────────────────────────────────────────────────

  // erase.line — straight strike-through
  'erase.line': {
    points: [
      [0.15, 0.50], [0.40, 0.48], [0.60, 0.52], [0.85, 0.50],
    ],
    closed: false,
  },

  // arcane.open — expanding spiral
  'arcane.open': {
    points: [
      [0.50, 0.50], [0.56, 0.42], [0.62, 0.46], [0.58, 0.56],
      [0.48, 0.60], [0.38, 0.52], [0.38, 0.40], [0.48, 0.30],
      [0.62, 0.30], [0.74, 0.42], [0.74, 0.58],
      [0.62, 0.72], [0.45, 0.74], [0.30, 0.60],
    ],
    closed: false,
  },

  // layer.up — upward arrow with chevron
  'layer.up': {
    points: [
      [0.50, 0.80], [0.50, 0.55], [0.50, 0.30],
      [0.38, 0.44], // left chevron barb
    ],
    closed: false,
  },

  // layer.down — downward arrow with chevron
  'layer.down': {
    points: [
      [0.50, 0.20], [0.50, 0.45], [0.50, 0.70],
      [0.38, 0.56], // left chevron barb
    ],
    closed: false,
  },

  // copy.block — circle over a block
  'copy.block': {
    points: [
      [0.38, 0.32], [0.28, 0.50], [0.38, 0.68],
      [0.62, 0.68], [0.72, 0.50], [0.62, 0.32], [0.38, 0.32],
    ],
    closed: true,
  },

  // paste.here — X cross mark
  'paste.here': {
    points: [
      [0.30, 0.30], [0.50, 0.50], [0.70, 0.30],
      [0.50, 0.50], [0.30, 0.70], [0.50, 0.50], [0.70, 0.70],
    ],
    closed: false,
  },

  // ── Lasso-arcane family ────────────────────────────────────────────────

  // select.region — plain lasso (closed loop)
  'select.region': {
    points: [
      [0.50, 0.15], [0.80, 0.25], [0.90, 0.50], [0.80, 0.75],
      [0.50, 0.85], [0.20, 0.75], [0.10, 0.50], [0.20, 0.25],
      [0.50, 0.15],
    ],
    closed: true,
  },

  // copy.region — lasso then check mark inside
  'copy.region': {
    points: [
      [0.50, 0.10], [0.80, 0.20], [0.90, 0.50], [0.80, 0.80],
      [0.50, 0.90], [0.20, 0.80], [0.10, 0.50], [0.20, 0.20],
      [0.50, 0.10],
      // check mark inside
      [0.35, 0.55], [0.45, 0.70], [0.65, 0.40],
    ],
    closed: false,
  },

  // snapshot.region — lasso then rectangle inside
  'snapshot.region': {
    points: [
      [0.50, 0.10], [0.80, 0.20], [0.90, 0.50], [0.80, 0.80],
      [0.50, 0.90], [0.20, 0.80], [0.10, 0.50], [0.20, 0.20],
      [0.50, 0.10],
      // small rectangle inside
      [0.38, 0.42], [0.62, 0.42], [0.62, 0.62], [0.38, 0.62], [0.38, 0.42],
    ],
    closed: false,
  },

  // arcane.eval — lasso with a rune-like symbol inside
  'arcane.eval': {
    points: [
      [0.50, 0.10], [0.80, 0.20], [0.90, 0.50], [0.80, 0.80],
      [0.50, 0.90], [0.20, 0.80], [0.10, 0.50], [0.20, 0.20],
      [0.50, 0.10],
      // star/rune inside
      [0.50, 0.35], [0.40, 0.65], [0.65, 0.45], [0.35, 0.45], [0.60, 0.65], [0.50, 0.35],
    ],
    closed: false,
  },
};

// ── Interpolation helpers ────────────────────────────────────────────────────

/** Sample a point along the polyline at parameter t ∈ [0, 1]. */
export function sampleCurve(curve: CastCurve, t: number): [number, number] {
  const pts = curve.points;
  if (pts.length === 0) return [0, 0];
  if (pts.length === 1) return pts[0];

  const clampedT = Math.max(0, Math.min(1, t));
  const totalSegs = pts.length - 1;
  const rawIdx = clampedT * totalSegs;
  const segIdx = Math.min(Math.floor(rawIdx), totalSegs - 1);
  const segT = rawIdx - segIdx;

  const a = pts[segIdx];
  const b = pts[segIdx + 1];
  return [
    a[0] + (b[0] - a[0]) * segT,
    a[1] + (b[1] - a[1]) * segT,
  ];
}

/** Compute the total arc length of a curve in normalized space. */
export function curveArcLength(curve: CastCurve): number {
  let len = 0;
  for (let i = 1; i < curve.points.length; i++) {
    const dx = curve.points[i][0] - curve.points[i - 1][0];
    const dy = curve.points[i][1] - curve.points[i - 1][1];
    len += Math.sqrt(dx * dx + dy * dy);
  }
  return len;
}
